/**
 * DisputeChat — three-way messaging thread for dispute resolution.
 *
 * Renders role-colored chat bubbles (buyer=sky, seller=orange, arbiter=gold)
 * with a sticky input bar. Supports auto-scroll to newest message, 8s
 * polling, and read-only mode when the dispute is resolved.
 *
 * Design spec: UI/UX Pro Max — "Modern Dark (Cinema Mobile)" style with
 * existing glassmorphism tokens. See tokens.css for --msg-buyer/--msg-seller.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Send, ShieldCheck } from 'lucide-react';

import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

import { disputes as disputesApi, isApiError } from '@/lib/api';
import type { DisputeMessage, DisputeMessageRole } from '@/lib/api/types';
import { useT } from '@/lib/i18n';
import { haptics } from '@/lib/telegram/haptics';
import { cn } from '@/lib/utils/cn';
import { formatDateTime } from '@/lib/utils/format';

// ─── Constants ─────────────────────────────────────────────

const POLL_INTERVAL_MS = 8_000;
const MAX_MESSAGE_LENGTH = 2000;

// ─── Role styling ──────────────────────────────────────────

const ROLE_STYLES: Record<DisputeMessageRole, {
  bubble: string;
  label: string;
  align: string;
}> = {
  buyer: {
    bubble: 'bg-msg-buyer/10 border-msg-buyer/30',
    label: 'text-msg-buyer',
    align: 'self-start',
  },
  seller: {
    bubble: 'bg-msg-seller/10 border-msg-seller/30',
    label: 'text-msg-seller',
    align: 'self-end',
  },
  arbiter: {
    bubble: 'bg-accent-gold/10 border-accent-gold/30 shadow-glow-gold',
    label: 'text-accent-gold',
    align: 'self-stretch',
  },
  admin: {
    bubble: 'bg-accent-gold/10 border-accent-gold/30 shadow-glow-gold',
    label: 'text-accent-gold',
    align: 'self-stretch',
  },
};

// ─── Types ─────────────────────────────────────────────────

interface DisputeChatProps {
  disputeId: string;
  currentUserId: string | null;
  isResolved: boolean;
}

// ─── Component ─────────────────────────────────────────────

export function DisputeChat({
  disputeId,
  currentUserId,
  isResolved,
}: DisputeChatProps): JSX.Element {
  const t = useT();

  // Message state
  const [messages, setMessages] = useState<DisputeMessage[]>([]);
  const [loading, setLoading] = useState(true);

  // Input state
  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  // Scroll ref
  const scrollRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);

  // ── Fetch messages ──────────────────────────────────────

  const fetchMessages = useCallback(async (): Promise<void> => {
    try {
      const { messages: rows } = await disputesApi.listMessages(disputeId);
      setMessages(rows);
    } catch {
      // Non-fatal — polling will retry.
    } finally {
      setLoading(false);
    }
  }, [disputeId]);

  // Initial load
  useEffect(() => {
    void fetchMessages();
  }, [fetchMessages]);

  // Polling
  useEffect(() => {
    if (isResolved) return;
    const interval = setInterval(() => void fetchMessages(), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchMessages, isResolved]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (isAtBottomRef.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    isAtBottomRef.current = scrollHeight - scrollTop - clientHeight < 40;
  }, []);

  // ── Send message ────────────────────────────────────────

  const handleSend = useCallback(async (): Promise<void> => {
    const trimmed = inputValue.trim();
    if (!trimmed || sending || isResolved) return;

    haptics.light();
    setSending(true);
    setSendError(null);

    try {
      const { message } = await disputesApi.postMessage(disputeId, {
        body: trimmed,
      });
      setMessages((prev) => [...prev, message]);
      setInputValue('');
      haptics.success();
      // Scroll to bottom after own message
      isAtBottomRef.current = true;
    } catch (err) {
      haptics.error();
      const msg = isApiError(err)
        ? err.message
        : t('dispute.phase.view.messages.errorSend');
      setSendError(msg);
    } finally {
      setSending(false);
    }
  }, [disputeId, inputValue, isResolved, sending, t]);

  // ── Render ──────────────────────────────────────────────

  if (loading) {
    return (
      <Card className="flex flex-col gap-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
          {t('dispute.phase.view.messages.title')}
        </h3>
        <div className="flex flex-col gap-2">
          <Skeleton className="h-16 w-3/4" />
          <Skeleton className="ml-auto h-16 w-3/4" />
          <Skeleton className="h-16 w-3/4" />
        </div>
      </Card>
    );
  }

  return (
    <Card className="flex flex-col gap-3 p-0 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-4">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
          {t('dispute.phase.view.messages.title')}
        </h3>
        <span className="text-[10px] text-text-muted">
          {messages.length > 0 ? `${messages.length}` : ''}
        </span>
      </div>

      {/* Message thread */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        role="log"
        aria-live="polite"
        className="flex max-h-[400px] flex-col gap-3 overflow-y-auto px-4 py-2 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-accent-purple/30"
      >
        {messages.length === 0 ? (
          <p className="py-6 text-center text-xs text-text-muted">
            {t('dispute.phase.view.messages.empty')}
          </p>
        ) : (
          messages.map((msg) => {
            const style = ROLE_STYLES[msg.senderRole] ?? ROLE_STYLES.buyer;
            const isOwn = currentUserId !== null && msg.senderId === currentUserId;
            const isArbiterOrAdmin =
              msg.senderRole === 'arbiter' || msg.senderRole === 'admin';

            return (
              <div
                key={msg.id}
                className={cn(
                  'flex flex-col gap-1 animate-fade-in',
                  isArbiterOrAdmin ? 'self-stretch' : style.align,
                  !isArbiterOrAdmin && 'max-w-[85%]',
                )}
                aria-label={`${t(`dispute.phase.view.messages.role.${msg.senderRole}` as const)} message`}
              >
                {/* Arbiter: full-width with gold left border */}
                <div
                  className={cn(
                    'rounded-lg border px-3 py-2',
                    style.bubble,
                    isArbiterOrAdmin && 'border-l-2 border-l-accent-gold',
                  )}
                >
                  {/* Sender label */}
                  <div className="mb-1 flex items-center gap-1.5">
                    <span className={cn('text-[10px] font-semibold uppercase tracking-wide', style.label)}>
                      {t(`dispute.phase.view.messages.role.${msg.senderRole}` as const)}
                    </span>
                    {isOwn && (
                      <span className="rounded-full bg-accent-purple/20 px-1.5 py-0.5 text-[8px] font-medium text-accent-purple">
                        {t('dispute.phase.view.messages.you')}
                      </span>
                    )}
                    {isArbiterOrAdmin && (
                      <ShieldCheck className="h-3 w-3 text-accent-gold" aria-hidden />
                    )}
                  </div>

                  {/* Body */}
                  <p className="whitespace-pre-wrap break-words text-sm text-text-secondary">
                    {msg.body}
                  </p>

                  {/* Timestamp */}
                  <span className="mt-1 block text-right text-[10px] text-text-muted">
                    {formatDateTime(msg.createdAt)}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Input bar or read-only notice */}
      {isResolved ? (
        <div className="border-t border-border/40 bg-bg-primary/60 px-4 py-3">
          <p className="text-center text-xs text-text-muted">
            {t('dispute.phase.view.messages.readOnly')}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-1 border-t border-border/40 bg-bg-primary/95 px-4 py-3 backdrop-blur-[16px]">
          <div className="flex items-end gap-2">
            <textarea
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                setSendError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void handleSend();
                }
              }}
              maxLength={MAX_MESSAGE_LENGTH}
              rows={1}
              placeholder={t('dispute.phase.view.messages.placeholder')}
              className={cn(
                'flex-1 resize-none rounded-lg border border-border bg-bg-secondary/60 px-3 py-2.5 text-sm text-text-primary placeholder:text-text-muted',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple',
                'min-h-[44px] max-h-[120px]',
              )}
              style={{ fieldSizing: 'content' } as React.CSSProperties}
            />
            <Button
              size="icon"
              onClick={() => void handleSend()}
              disabled={sending || inputValue.trim().length === 0}
              className={cn(
                'h-[44px] w-[44px] shrink-0 rounded-lg transition-colors',
                inputValue.trim().length > 0 && !sending
                  ? 'bg-accent-purple text-white hover:bg-accent-purple/80 active:scale-[0.95]'
                  : 'bg-bg-tertiary text-text-muted',
              )}
              aria-label={t('dispute.phase.view.messages.send')}
            >
              {sending ? (
                <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
              ) : (
                <Send className="h-5 w-5" aria-hidden />
              )}
            </Button>
          </div>

          {/* Char count + error */}
          <div className="flex items-center justify-between">
            {sendError ? (
              <p className="text-xs text-danger">{sendError}</p>
            ) : (
              <span />
            )}
            <span
              className={cn(
                'text-[10px]',
                inputValue.length > 1800 ? 'text-danger' : 'text-text-muted',
              )}
            >
              {inputValue.length > 0
                ? `${inputValue.length}/${MAX_MESSAGE_LENGTH}`
                : ''}
            </span>
          </div>
        </div>
      )}
    </Card>
  );
}
